import React, { useState, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCloudUploadAlt } from '@fortawesome/free-solid-svg-icons';
import AddFormModal from '../AddFormModal/AddFormModal';
import './EmailModal.css';

const EmailModal = ({ isOpen, onClose, onSend, defaultSubject = '', defaultContent = '' }) => {
  const [emailData, setEmailData] = useState({
    to: '',
    cc: '',
    bcc: '',
    subject: defaultSubject || '',
    content: defaultContent || '',
    attachments: []
  });
  const [errors, setErrors] = useState({});
  const fileInputRef = useRef(null);

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const hasInvalidEmail = (value) => {
    const emails = String(value || '')
      .split(',')
      .map((e) => e.trim())
      .filter(Boolean);
    return emails.some((e) => !emailRegex.test(e));
  };

  const getFieldError = (field, value) => {
    if (field === 'to') {
      if (!String(value || '').trim()) return 'Email address is required';
      if (hasInvalidEmail(value)) return 'Please enter a valid email address';
      return '';
    }
    if (field === 'cc' || field === 'bcc') {
      if (String(value || '').trim() && hasInvalidEmail(value)) {
        return 'Please enter a valid email address';
      }
      return '';
    }
    if (field === 'subject') {
      return String(value || '').trim() ? '' : 'Subject is required';
    }
    if (field === 'content') {
      return String(value || '').trim() ? '' : 'Content is required';
    }
    return '';
  };

  const handleChange = (field, value) => {
    setEmailData(prev => ({
      ...prev,
      [field]: value
    }));
    setErrors(prev => ({
      ...prev,
      [field]: getFieldError(field, value)
    }));
  };

  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 0) {
      setEmailData(prev => ({
        ...prev,
        attachments: [...prev.attachments, ...files]
      }));
    }
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const removeAttachment = (index) => {
    setEmailData(prev => ({
      ...prev,
      attachments: prev.attachments.filter((_, i) => i !== index)
    }));
  };

  const validate = () => {
    const newErrors = {};
    
    ['to', 'cc', 'bcc', 'subject', 'content'].forEach((field) => {
      const message = getFieldError(field, emailData[field]);
      if (message) newErrors[field] = message;
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSend = () => {
    if (validate()) {
      onSend(emailData);
    }
  };

  const handleCancel = () => {
    setEmailData({
      to: '',
      cc: '',
      bcc: '',
      subject: defaultSubject || '',
      content: defaultContent || '',
      attachments: []
    });
    setErrors({});
    onClose();
  };

  return (
    <AddFormModal
      isOpen={isOpen}
      onClose={handleCancel}
      title="Email"
      className="email-modal-overlay"
      footer={(
        <div className="email-modal-footer">
          <button type="button" className="email-btn-cancel" onClick={handleCancel}>
            Cancel
          </button>
          <button type="button" className="email-btn-send" onClick={handleSend}>
            Email
          </button>
        </div>
      )}
    >
      <div className="email-modal-body">
          <div className="email-form-row">
            <div className="email-form-field">
              <label>To <span className="required">*</span></label>
              <input
                type="text"
                className={`email-input ${errors.to ? 'error' : ''}`}
                value={emailData.to}
                onChange={(e) => handleChange('to', e.target.value)}
                placeholder="Enter email address"
              />
              {errors.to && <div className="error-message">{errors.to}</div>}
            </div>
            <div className="email-form-field">
              <label>CC</label>
              <input
                type="text"
                className={`email-input ${errors.cc ? 'error' : ''}`}
                value={emailData.cc}
                onChange={(e) => handleChange('cc', e.target.value)}
                placeholder="Enter email address"
              />
              {errors.cc && <div className="error-message">{errors.cc}</div>}
            </div>
          </div>

          <div className="email-form-row">
            <div className="email-form-field">
              <label>BCC</label>
              <input
                type="text"
                className={`email-input ${errors.bcc ? 'error' : ''}`}
                value={emailData.bcc}
                onChange={(e) => handleChange('bcc', e.target.value)}
                placeholder="Enter email address"
              />
              {errors.bcc && <div className="error-message">{errors.bcc}</div>}
            </div>
            <div className="email-form-field">
              <label>Subject <span className="required">*</span></label>
              <input
                type="text"
                className={`email-input ${errors.subject ? 'error' : ''}`}
                value={emailData.subject}
                onChange={(e) => handleChange('subject', e.target.value)}
                placeholder="Enter Subject"
              />
              {errors.subject && <div className="error-message">{errors.subject}</div>}
            </div>
          </div>

          <div className="email-form-row">
            <div className="email-form-field email-form-field-full">
              <label>Attachments</label>
              <div className="email-attachment-container">
                <input
                  type="text"
                  className="email-input email-attachment-input"
                  readOnly
                  placeholder="No file selected"
                  value=""
                />
                <button
                  type="button"
                  className="email-upload-btn"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <FontAwesomeIcon icon={faCloudUploadAlt} /> Upload File
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  style={{ display: 'none' }}
                  onChange={handleFileUpload}
                />
              </div>
              {emailData.attachments.length > 0 && (
                <div className="email-attachments-list">
                  {emailData.attachments.map((file, index) => (
                    <div key={index} className="email-attachment-item">
                      <span className="email-attachment-name">{file.name}</span>
                      <button
                        type="button"
                        className="email-attachment-remove"
                        onClick={() => removeAttachment(index)}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="email-form-row">
            <div className="email-form-field email-form-field-full">
              <label>Content <span className="required">*</span></label>
              <textarea
                className={`email-textarea ${errors.content ? 'error' : ''}`}
                value={emailData.content}
                onChange={(e) => handleChange('content', e.target.value)}
                placeholder="Content.."
                rows="8"
              />
              {errors.content && <div className="error-message">{errors.content}</div>}
            </div>
          </div>
      </div>
    </AddFormModal>
  );
};

export default EmailModal;

